import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot, Router, UrlTree } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { firstValueFrom, combineLatest } from 'rxjs';
import { map, skipWhile, filter, take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

// Import Akita queries and facades
import { AuthQuery } from '../state/auth.query';
import { UserQuery } from '../state/user.query';
import { UserFacadeService } from '../state/user-facade.service';
import { AuthService } from '../services/auth.service';

export const NO_TEFTER_ACCOUNT = 'NO_TEFTER_ACCOUNT';
export const NO_USER_ID = 'NO_USER';

interface SessionData {
  uid: string;
  timestamp: number;
  registered: boolean;
  orgId: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthGuard {
  private router = inject(Router);
  private authQuery = inject(AuthQuery);
  private userQuery = inject(UserQuery);
  private userFacade = inject(UserFacadeService);
  private authService = inject(AuthService);

  // Track guard check attempts to prevent infinite loops
  private attemptCount = 0;
  private readonly MAX_ATTEMPTS = 3;

  async canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
    const currentRoute = state.url;
    this.attemptCount++;

    try {
      // If we've tried too many times, redirect to login
      if (this.attemptCount > this.MAX_ATTEMPTS) {
        console.warn('🔄 AuthGuard: Too many activation attempts, redirecting to login');
        this.attemptCount = 0;
        return this.router.parseUrl('/auth');
      }

      // Wait for both auth and user to be initialized
      const authData = await firstValueFrom(
        combineLatest([
          this.userQuery.user$,
          this.authQuery.idToken$,
          this.authQuery.initialized$,
          this.userQuery.initialized$,
        ]).pipe(
          // Wait until both auth and user are initialized
          skipWhile(([user, token, authInit, userInit]) => {
            // Still waiting for initialization
            if (!authInit || !userInit) {
              console.log('⏳ AuthGuard: Waiting for initialization...');
              return true;
            }

            // No token available
            if (!token) {
              const previousSession = this.getPreviousSessionData();
              if (previousSession) {
                console.log('⏳ AuthGuard: User previously authenticated, waiting for token...');
                if (this.attemptCount <= 2) {
                  this.reloadAuthData();
                }
                return true;
              }
            }

            // Token/user mismatch check
            if (token && user && user.id !== token.claims.sub) {
              console.warn('⚠️ AuthGuard: User mismatch detected, clearing session');
              localStorage.removeItem('userSessionData');
              return true;
            }

            // If user is undefined but we have previous session
            if (!user) {
              const previousSession = this.getPreviousSessionData();
              if (previousSession) {
                console.log('⏳ AuthGuard: Previous session exists, loading user data...');
                if (this.attemptCount <= 2) {
                  this.userFacade.initialize(previousSession.uid);
                }
                return true;
              }
            }

            // Ready to proceed
            return false;
          }),
          map(([user, token, authInit, userInit]) => {
            if (!token) {
              console.warn('🚫 AuthGuard: No valid token. Redirecting to /auth.');
              return this.navigateToSpecificPage(currentRoute, '/auth');
            }

            // Handle previously authenticated users with loading delays
            if (!user || !user.id || user.id === NO_USER_ID) {
              const previousSession = this.getPreviousSessionData();

              // Previously authenticated user
              if (previousSession && Date.now() - previousSession.timestamp < 24 * 60 * 60 * 1000) {
                console.log('⚠️ AuthGuard: Previously authenticated user with data loading delay.');

                // If max attempts reached, proceed anyway
                if (this.attemptCount >= this.MAX_ATTEMPTS - 1) {
                  console.log('⚠️ AuthGuard: Max attempts reached, proceeding with navigation.');
                  this.attemptCount = 0;
                  return true;
                }

                // Reload user data
                this.userFacade.initialize(previousSession.uid);
                return true;
              } else if (user?.registrationStatus === NO_TEFTER_ACCOUNT) {
                // New user needs registration
                console.warn('🚫 AuthGuard: New user needs registration.');
                return this.navigateToSpecificPage(currentRoute, '/account/register-user');
              }
            }

            // Check organization
            if (user && !user.orgId) {
              console.warn('🚫 AuthGuard: No organization.');
              return this.navigateToSpecificPage(currentRoute, '/account/register-organization');
            }

            // Check token organization claims
            if (!token.claims.orgId && !token.claims.orgIds) {
              console.warn('🚫 AuthGuard: No organization claims.');
              return this.navigateToSpecificPage(currentRoute, '/auth');
            }

            // Success! Save session and proceed
            if (user) {
              this.saveUserSessionState(user);
            }

            this.attemptCount = 0;
            console.log('✅ AuthGuard: User authenticated. Proceeding...');

            if (environment.production && user) {
              Sentry.setUser({
                id: user.id,
                username: user.phoneNumber,
                name: user.fullName,
              });
            }

            return true;
          }),
          take(1)
        )
      );

      return authData;
    } catch (error) {
      console.error('🚨 AuthGuard: Error occurred', error);

      // Check cached session on error
      const previousSession = this.getPreviousSessionData();
      if (previousSession && Date.now() - previousSession.timestamp < 24 * 60 * 60 * 1000) {
        console.log('⚠️ AuthGuard: Error occurred but using cached session');
        this.attemptCount = 0;
        return true;
      }

      return this.router.parseUrl('/auth');
    }
  }

  /**
   * Reload auth data
   */
  private async reloadAuthData(): Promise<void> {
    console.log('🔄 AuthGuard: Reloading auth data');
    try {
      await this.authService.getFreshIdToken();
    } catch (error) {
      console.error('🚨 AuthGuard: Error reloading auth data', error);
    }
  }

  private navigateToSpecificPage(currentRoute: string, urlPath: string): UrlTree {
    console.log(`🔄 AuthGuard: Redirecting from ${currentRoute} to ${urlPath}`);
    return this.router.parseUrl(urlPath);
  }

  /**
   * Save user session state to localStorage
   */
  private saveUserSessionState(user: any) {
    if (user && user.id && user.id !== NO_USER_ID && user.registrationStatus !== NO_TEFTER_ACCOUNT) {
      localStorage.setItem(
        'userSessionData',
        JSON.stringify({
          uid: user.id,
          timestamp: Date.now(),
          registered: true,
          orgId: user.orgId,
        })
      );
    }
  }

  /**
   * Get previous session data from localStorage
   */
  private getPreviousSessionData(): SessionData | null {
    try {
      const sessionData = localStorage.getItem('userSessionData');
      return sessionData ? JSON.parse(sessionData) : null;
    } catch (error) {
      console.error('Error parsing session data', error);
      return null;
    }
  }
}
